import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { getThemeVariant } from '../../../utils/styles';
import Font from '../Font';
import { getIconSize } from '../Icon';
import { getButton, getDefaultIconColor } from './buttonUtils';
import { ButtonLink, ButtonWrapper, CornerIcon, CustomIcon } from './styles';
import { ButtonProps, ButtonTheme, ButtonType2 } from './types';

/**
 * Button component.
 *
 * @component
 *
 * @example
 * // Render a button with a click handler
 * <Button onClick={() => console.log('Button clicked!')}>
 *   Click me!
 * </Button>
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Button content
 * @param {function} [props.onClick] - Click handler
 * @param {string} [props.className] - Custom class name
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 *
 * @returns {ReactElement} The Button component
 */

const Button: React.FC<ButtonProps> = ({
  as,
  children,
  cornerIcon,
  cornerIconSize = 'xs',
  customConfiguration,
  dataEvent,
  disabled,
  hasHover = true,
  iconColor,
  iconName,
  iconPosition = 'right',
  iconSize,
  isIconFilled = true,
  isLoading = false,
  name,
  onLinkClick,
  theme,
  variant,
  ...otherProps
}) => {
  const scTheme = useTheme();

  const loadedTheme: ButtonTheme =
    theme || getThemeVariant(scTheme.themeName) || 'dark';

  const defaultIconColor =
    variant !== 'iconLink'
      ? getDefaultIconColor(loadedTheme, disabled, variant)
      : undefined;

  const [iconColorValue, setIconColorValue] = useState(defaultIconColor);
  const buttonRef = useRef(null);

  const CustomButton = getButton(variant as ButtonType2);

  useEffect(() => {
    setIconColorValue(defaultIconColor);
  }, [disabled]);

  const { t } = useTranslation();

  const showLeftIcon =
    iconName && (iconPosition === 'left' || iconPosition === 'both');
  const showRightIcon =
    iconName && (iconPosition === 'right' || iconPosition === 'both');

  if (variant === 'iconLink') {
    return (
      <ButtonLink
        {...otherProps}
        $customConfiguration={customConfiguration}
        data-event={dataEvent}
        type="button"
      >
        {showLeftIcon && (
          <CustomIcon
            iconColor={iconColor}
            iconName={iconName}
            iconSize={iconSize}
          />
        )}

        {children && (
          <Font
            as={as || 'p'}
            customConfiguration={{
              ...customConfiguration,
              color: customConfiguration?.color || 'inherit',
              cursor: disabled ? 'unset' : 'pointer'
            }}
            onClick={(event) =>
              onLinkClick?.(event as MouseEvent<HTMLLinkElement>)
            }
            variant="bodySInteractive"
          >
            {children}
          </Font>
        )}

        {showRightIcon && (
          <CustomIcon
            iconColor={iconColor}
            iconName={iconName}
            iconSize={iconSize}
          />
        )}
      </ButtonLink>
    );
  }

  return (
    <ButtonWrapper $customConfiguration={customConfiguration}>
      <CustomButton
        $buttonTheme={loadedTheme}
        $customConfiguration={customConfiguration}
        $hasHover={hasHover}
        $hasIconOnly={!children}
        $isIconFilled={isIconFilled}
        data-event={dataEvent}
        disabled={!!disabled}
        name={name}
        ref={buttonRef}
        type="button"
        {...otherProps}
      >
        {showLeftIcon && (
          <CustomIcon
            iconColor={iconColor || iconColorValue}
            iconName={iconName}
            iconSize={iconSize}
          />
        )}
        {isLoading && <Font variant="bodyRegular">{t('Loading')}</Font>}
        {children && (
          <Font
            as={as || 'p'}
            customConfiguration={{
              color: customConfiguration?.color || 'inherit',
              cursor: disabled ? 'unset' : 'pointer',
              textTransform: customConfiguration?.textTransform || 'uppercase',
              whiteSpace: customConfiguration?.whiteSpace || 'nowrap'
            }}
            variant="textLinkRegular"
          >
            {children}
          </Font>
        )}
        {showRightIcon && (
          <CustomIcon
            iconColor={iconColor || iconColorValue}
            iconName={iconName}
            iconSize={iconSize}
          />
        )}
      </CustomButton>
      {cornerIcon && (
        <CornerIcon
          customsize={getIconSize(cornerIconSize)}
          iconName={cornerIcon}
          iconSize={cornerIconSize}
        />
      )}
    </ButtonWrapper>
  );
};

export default Button;
